import React, { useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { motion } from "framer-motion";
import { FaPlusCircle, FaRegTrashAlt, FaImage, FaVideo } from "react-icons/fa";
import axios from "axios";
import api from '../../../Axios'


export default function AddEvent() {
  const [loading, setLoading] = useState(false);
  const [event, setEvent] = useState({
    title: "",
    description: "",
    date: "",
    location: "",
    organizer: "",
    images: [],
    videos: [],
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEvent((prevEvent) => ({
      ...prevEvent,
      [name]: value,
    }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (name === "images" && files && files[0]) {
      setEvent((prevEvent) => ({
        ...prevEvent,
        images: [...prevEvent.images, ...Array.from(files)],
      }));
    } else if (name === "videos" && files) {
      setEvent((prevEvent) => ({
        ...prevEvent,
        videos: [...prevEvent.videos, ...Array.from(files)],
      }));
    }
  };

  const handleRemoveVideo = (index) => {
    setEvent((prevEvent) => ({
      ...prevEvent,
      videos: prevEvent.videos.filter((_, i) => i !== index),
    }));
  };

  const handleAddEvent = async (e) => {
    e.preventDefault();
    setLoading(true);
  
    try {
      const formData = new FormData();
      Object.entries(event).forEach(([key, value]) => {
        if (key === "images" && Array.isArray(value)) {
          value.forEach((image) => formData.append("images", image));
        } else if (key === "videos" && Array.isArray(value)) {
          value.forEach((video) => formData.append("videos", video));
        } else {
          formData.append(key, value);
        }
      });
      

      const response = await api.post(`${process.env.REACT_APP_BASE_URL}/api/v1/events/create`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },        
      });

      // Check for success based on status code
      if (response.status === 201) { // Assuming 201 is the success status for creation
        toast.success("Event added successfully!");
  
        // Reset the form state explicitly
        setEvent({
          title: "",
          description: "",
          date: "",
          location: "",
          organizer: "",
          image: null,
          videos: [],
        });
  
        // Clear file input fields manually
        const imageInput = document.getElementById("images");
        const videoInput = document.getElementById("videos");
        if (imageInput) imageInput.value = null;
        if (videoInput) videoInput.value = null;
      } else {
        throw new Error("Failed to add event");
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to add event. Please try again.");
    } finally {
      setLoading(false);
    }
};
  

  return (
    <motion.div
      className="flex items-center justify-center min-h-screen p-6"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      <div className="w-full max-w-4xl p-8 space-y-6 shadow-lg rounded-xl">
        <h2 className="mb-6 text-4xl font-semibold text-center text-teal-600">Add New Event</h2>

        <form onSubmit={handleAddEvent} className="space-y-6">
          {/* Event Title */}
          <div className="space-y-2">
            <label htmlFor="title" className="block text-lg font-medium text-teal-600">Event Title</label>
            <input
              type="text"
              id="title"
              name="title"
              value={event.title}
              onChange={handleInputChange}
              className="w-full p-4 border-2 border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Enter event title"
              required
            />
          </div>

          {/* Event Description */}
          <div className="space-y-2">
            <label htmlFor="description" className="block text-lg font-medium text-teal-600">Event Description</label>
            <textarea
              id="description"
              name="description"
              value={event.description}
              onChange={handleInputChange}
              className="w-full p-4 border-2 border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              rows="4"
              placeholder="Enter event description"
            />
          </div>

          {/* Event Date */}
          <div className="space-y-2">
            <label htmlFor="date" className="block text-lg font-medium text-teal-600">Event Date</label>
            <input
              type="date"
              id="date"
              name="date"
              value={event.date}
              onChange={handleInputChange}
              className="w-full p-4 border-2 border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              required
            />
          </div>

          {/* Event Location */}
          <div className="space-y-2">
            <label htmlFor="location" className="block text-lg font-medium text-teal-600">Event Location</label>
            <input
              type="text"
              id="location"
              name="location"
              value={event.location}
              onChange={handleInputChange}
              className="w-full p-4 border-2 border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Enter event location"
              required
            />
          </div>

          {/* Event Organizer */}
          <div className="space-y-2">
            <label htmlFor="organizer" className="block text-lg font-medium text-teal-600">Event Organizer</label>
            <input
              type="text"
              id="organizer"
              name="organizer"
              value={event.organizer}
              onChange={handleInputChange}
              className="w-full p-4 border-2 border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Enter event organizer"
              required
            />
          </div>

          {/* Event Image */}
          <div className="space-y-2">
            <label htmlFor="image" className="block text-lg font-medium text-teal-600">Event Image</label>
            <div className="flex items-center space-x-2">
              <input
                type="file"
                id="images"
                name="images"
                onChange={handleFileChange}
                className="w-full p-4 border-2 border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                accept="image/*"
              />
              <FaImage className="text-2xl text-teal-600" />
            </div>
            {/* {event.images && (
              <p className="text-sm text-gray-500">
                Selected image: {event.image.name}
              </p>
            )} */}
          </div>

          {/* Event Videos */}
          <div className="space-y-2">
            <label htmlFor="videos" className="block text-lg font-medium text-teal-600">Event Videos</label>
            <div className="flex items-center space-x-2">
              <input
                type="file"
                id="videos"
                name="videos"
                onChange={handleFileChange}
                className="w-full p-4 border-2 border-teal-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                accept="video/*"
                multiple
              />
              <FaVideo className="text-2xl text-teal-600" />
            </div>
            {event.videos.length > 0 && (
              <div className="mt-2">
                <p className="mb-1 text-sm font-medium">Selected videos:</p>
                <ul className="list-disc list-inside">
                  {event.videos.map((video, index) => (
                    <li key={index} className="flex items-center justify-between">
                      <span>{video.name}</span>
                      <button
                        type="button"
                        className="text-red-500 hover:text-red-700"
                        onClick={() => handleRemoveVideo(index)}
                      >
                        <FaRegTrashAlt />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-4">
            <button
              type="button"
              onClick={() =>
                setEvent({
                  title: "",
                  description: "",
                  date: "",
                  location: "",
                  organizer: "",
                  image: null,
                  videos: [],
                })
              }
              className="w-full px-4 py-3 text-teal-600 transition-all rounded-lg shadow-md hover:bg-teal-200"
            >
              <FaRegTrashAlt className="inline-block mr-2" />
              Clear
            </button>

            <button
              type="submit"
              disabled={loading}
              className="w-full px-4 py-3 text-white transition-all bg-teal-600 rounded-lg shadow-md hover:bg-teal-700"
            >
              {loading ? "Adding..." : (
                <>
                  <FaPlusCircle className="inline-block mr-2" />
                  Add Event
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      <ToastContainer position="top-right" autoClose={5000} />
    </motion.div>
  );
}

